import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { LogQuery } from "@jkast90/kast-common/admin-core";
import { admin, adminLogsQuery, adminQuery } from "./admin";
import { cloudSaves } from "../emulator/cloudSaves";
import { listCloudRoms } from "./cloudLibrary";
import { useSignedIn } from "@jkast90/kast-common/auth";
import { signedInAdmin } from "./admin";
import { coverFor } from "./covers";
import { getDashboard, playerPicture } from "./dashboard";
import { myPlaytime } from "./playtime";

// The server data the pages show, cached by the query client. The sync
// engine and the emulator hook read the API directly: they want it fresh.
export const queryKeys = {
  library: ["library"] as const,
  dashboard: ["dashboard"] as const,
  playtime: ["playtime"] as const,
  /** The signed-in account's pool of profile pictures (kast-dev-admin's API). */
  pictures: ["me", "pictures"] as const,
  /** Every player's saves as the admin page shows them; a prefix of adminSaves. */
  adminAllSaves: ["admin", "saves"] as const,
  adminSaves: (sub: string) => ["admin", "saves", sub] as const,
  /** The signed-in account's own cloud saves, as the library page shows them. */
  cloudSaves: ["cloudSaves"] as const,
};

/** Everything a library change (an upload, a rename, a removal) can alter. */
export const libraryKeys = [queryKeys.library, queryKeys.dashboard, queryKeys.playtime];
/** A rename or removal also moves or drops every player's saves of the game. */
export const romChangeKeys = [...libraryKeys, queryKeys.adminAllSaves, queryKeys.cloudSaves];
/** A change to someone's cloud saves: theirs as the admin page lists them, and the dashboard's last-save times. */
export const saveKeys = (sub: string, mine: boolean) => [
  queryKeys.adminSaves(sub),
  queryKeys.dashboard,
  ...(mine ? [queryKeys.cloudSaves] : []),
];

export const useCloudRoms = (enabled = true) =>
  useQuery({ queryKey: queryKeys.library, queryFn: listCloudRoms, enabled });
export const useDashboard = (enabled = true) =>
  useQuery({ queryKey: queryKeys.dashboard, queryFn: getDashboard, enabled });
/**
 * The signed-in account's picture for the header, as the profile page shows
 * it: the pool picture, else the chosen (or most played) game's cover. From
 * the shared dashboard feed, asked for only while signed in.
 */
export const useMyPicture = (): string | null => {
  const signedIn = useSignedIn();
  const feed = useDashboard(signedIn).data;
  const picture = signedIn ? playerPicture(feed, signedInAdmin().sub) : null;
  const game = picture?.kind === "cover" ? picture.game : null;
  // A cover's URL comes from the covers index, looked up once per game.
  const [cover, setCover] = useState<{ game: string; url: string | null } | null>(null);
  useEffect(() => {
    if (!game) return;
    let live = true;
    void coverFor(game).then((url) => live && setCover({ game, url }));
    return () => {
      live = false;
    };
  }, [game]);
  if (picture?.kind === "pool") return picture.url;
  return game && cover?.game === game ? cover.url : null;
};
export const useMyPlaytime = (enabled = true) =>
  useQuery({ queryKey: queryKeys.playtime, queryFn: myPlaytime, enabled });
export const useAdminDeploys = (enabled = true) => useQuery({ ...adminQuery.deploys, enabled });
export const useAdminLambdas = (enabled = true) => useQuery({ ...adminQuery.lambdas, enabled });
export const useAdminMembers = (enabled = true) => useQuery({ ...adminQuery.members, enabled });
export const useAdminSaves = (sub: string) =>
  useQuery({ queryKey: queryKeys.adminSaves(sub), queryFn: () => admin.saves(sub) });
/** Logs are read when asked for (and on Refresh), never served stale. */
export const useAdminLogs = (query: LogQuery) => useQuery({ ...adminLogsQuery(query), staleTime: 0 });
/** Fresh on every visit (no stale time): they change with every save. */
export const useCloudSaves = (enabled = true) =>
  useQuery({ queryKey: queryKeys.cloudSaves, queryFn: () => cloudSaves.list(), enabled, staleTime: 0 });
export const useAdminVisitors = () => useQuery(adminQuery.visitors());
